// The Awakening, told before the founding form (playtest 09.10: « hard to get into the universe, the story »). Four
// beats on a drawing of the Network: lit by the Ancients, put out by the Silence, one relay flickering back, the
// Generals waking. Shown once, skippable at every beat, replayable from the landing; reduced motion = text only.
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';

const SEEN = 'aurane.prologue';
export function prologueSeen(): boolean { try { return localStorage.getItem(SEEN) === '1'; } catch { return true; } }
function markSeen(): void { try { localStorage.setItem(SEEN, '1'); } catch { /* ignore */ } }

interface Node { x: number; y: number; r: number }
interface Edge { a: number; b: number; d: number }

/** A deterministic little galaxy: nodes on a disc, each tied to its three nearest neighbours (one web, not islands). */
function network(n = 46): { nodes: Node[]; edges: Edge[] } {
  let s = 7;
  const rnd = (): number => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  const nodes: Node[] = [];
  while (nodes.length < n) {
    const a = rnd() * Math.PI * 2, d = Math.sqrt(rnd()) * 0.46;
    const x = 0.5 + Math.cos(a) * d, y = 0.5 + Math.sin(a) * d * 0.72;
    if (nodes.every((o) => Math.hypot(o.x - x, o.y - y) > 0.045)) nodes.push({ x, y, r: 1 + rnd() * 1.6 });
  }
  const seen = new Set<string>();
  const edges: Edge[] = [];
  nodes.forEach((p, i) => {
    nodes.map((q, j) => ({ j, d: Math.hypot(p.x - q.x, p.y - q.y) })).filter((o) => o.j !== i).sort((u, v) => u.d - v.d).slice(0, 3).forEach(({ j }) => {
      const k = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (!seen.has(k)) { seen.add(k); edges.push({ a: i, b: j, d: Math.hypot(nodes[i]!.x - 0.5, nodes[i]!.y - 0.5) + Math.hypot(nodes[j]!.x - 0.5, nodes[j]!.y - 0.5) }); }
    });
  });
  return { nodes, edges };
}

const BEATS = ['prologue1', 'prologue2', 'prologue3', 'prologue4'] as const;

export function Prologue({ onDone }: { onDone: () => void }) {
  const [beat, setBeat] = useState(0);
  const canvas = useRef<HTMLCanvasElement>(null);
  const beatRef = useRef(0);
  const since = useRef(performance.now());
  beatRef.current = beat;
  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finish = (): void => { markSeen(); onDone(); };
  const next = (): void => { if (beat < BEATS.length - 1) { setBeat(beat + 1); since.current = performance.now(); } else finish(); };

  useEffect(() => {
    const cv = canvas.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;
    const { nodes, edges } = network();
    const maxD = Math.max(...edges.map((e) => e.d));
    // The relay that wakes first: the edge nearest the centre.
    const first = edges.reduce((m, e, i) => (e.d < edges[m]!.d ? i : m), 0);
    let raf = 0;
    const draw = (now: number): void => {
      const w = cv.clientWidth, h = cv.clientHeight, dpr = Math.min(2, window.devicePixelRatio || 1);
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const b = beatRef.current;
      const el = reduced ? 99 : (now - since.current) / 1000; // seconds into the beat
      // The drawing keeps to the upper two thirds: the text sits below it, never on it.
      const S = Math.min(w * 0.92, h * 0.95);
      const ox = (w - S) / 2, oy = h * 0.05 - S * 0.17;
      const px = (n: Node): [number, number] => [ox + n.x * S, oy + n.y * S];
      const lit = (e: Edge, i: number): number => {
        if (b === 0) return Math.min(1, Math.max(0, el * 1.4 - (e.d / maxD) * 1.2)); // the Network spreads from the core
        if (b === 1) return Math.max(0, 1 - Math.max(0, el * 0.9 - (1 - e.d / maxD) * 1.6)); // dark from the edge in
        if (b === 2) return i === first ? (el < 1.6 ? (Math.sin(el * 14) > 0.2 ? 0.9 : 0.15) : 1) : 0; // one relay flickers back
        return i === first ? 1 : 0.12 * Math.min(1, el / 2); // the rest is a memory
      };
      edges.forEach((e, i) => {
        const v = lit(e, i);
        const [x1, y1] = px(nodes[e.a]!), [x2, y2] = px(nodes[e.b]!);
        ctx.strokeStyle = `rgba(125, 211, 252, ${0.06 + v * 0.7})`;
        ctx.lineWidth = 0.6 + v * 1.4;
        ctx.shadowColor = 'rgba(125, 211, 252, 0.9)'; ctx.shadowBlur = v * 10;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      });
      ctx.shadowBlur = 0;
      nodes.forEach((n, i) => {
        const touching = edges.some((e, k) => (e.a === i || e.b === i) && lit(e, k) > 0.5);
        const [x, y] = px(n);
        ctx.fillStyle = touching ? 'rgba(232, 244, 255, 0.95)' : 'rgba(160, 176, 208, 0.35)';
        ctx.beginPath(); ctx.arc(x, y, n.r, 0, Math.PI * 2); ctx.fill();
      });
      // The Generals: four embers in the ruins, one answering.
      if (b === 3) {
        const e = edges[first]!;
        [0, 9, 21, 33].forEach((ni, k) => {
          const [x, y] = px(nodes[k === 0 ? e.a : ni % nodes.length]!);
          const pulse = 0.5 + 0.5 * Math.sin(el * 2.4 + k * 1.3);
          ctx.fillStyle = `rgba(252, 196, 104, ${(k === 0 ? 0.6 : 0.45) + pulse * 0.35})`;
          ctx.shadowColor = 'rgba(252, 196, 104, 1)'; ctx.shadowBlur = 14 + pulse * 10;
          ctx.beginPath(); ctx.arc(x, y, k === 0 ? 5 : 3.5, 0, Math.PI * 2); ctx.fill();
        });
        ctx.shadowBlur = 0;
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [beat, reduced]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') finish(); else if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') { e.preventDefault(); next(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div class="prologue" onClick={next} role="dialog" aria-label={t('prologueTitle')}>
      <canvas ref={canvas} aria-hidden="true" />
      <div class="prologue-text" key={beat} aria-live="polite">
        <p class="prologue-step">{BEATS.map((_, i) => <span key={i} class={i <= beat ? 'on' : ''} />)}</p>
        <p>{t(BEATS[beat]!)}</p>
      </div>
      <div class="prologue-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" class="link" onClick={finish}>{t('prologueSkip')}</button>
        <button type="button" class="primary" onClick={next}>{beat < BEATS.length - 1 ? t('prologueNext') : t('prologueFound')}</button>
      </div>
    </div>
  );
}
